import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError } from "../adminApi";
import { HSA_YEAR, INDUSTRIES, SAMPLE, model, money, reason, signed, top3 } from "../../lib/planModel";
import { ASSUMPTIONS, CHECKLIST, FORMULAS } from "../planBuilderRef";

const PRINT_CSS = `
@media print {
  .pb-no-print { display: none !important; }
  @page { size: letter; margin: 0.6in; }
  .pb-card { break-inside: avoid; page-break-inside: avoid; }
  body { background: #ffffff !important; }
}`;

const th = "border-b border-[#D8D2C4] px-2 py-2 text-left text-[11px] uppercase tracking-[0.12em] text-[#4A524D]";
const td = "border-b border-[#EDE9DF] px-2 py-2 text-[13px] text-[#111812]";

export default function AdminPlanSummary() {
  const { scenarioId } = useParams();
  const [sc, setSc] = useState(null);
  const [dollars, setDollars] = useState(true);
  const [showAssumptions, setShowAssumptions] = useState(false);

  useEffect(() => {
    adminApi.get(`/crm/plan-builder/scenarios/${scenarioId}`)
      .then(({ data }) => setSc(data)).catch((e) => toast.error(apiError(e)));
  }, [scenarioId]);

  if (!sc) return <p className="text-sm text-cream/50" data-testid="pb-summary-loading">Loading…</p>;

  const inputs = { ...SAMPLE, ...sc.inputs };
  const m = model(inputs);
  const t3 = top3(m);
  const hsa = HSA_YEAR[inputs.year];
  const industry = (INDUSTRIES.find((i) => i.value === inputs.industry) || {}).label;
  const priorities = [["pCost", "cost containment"], ["pRecruit", "recruiting and retention"],
    ["pSimple", "simplicity"], ["pChoice", "employee choice"]]
    .filter(([key]) => inputs[key]).map(([, text]) => text).join(", ") || "none selected";
  const prepared = new Date().toLocaleDateString("en-US", { timeZone: "America/Chicago", dateStyle: "long" });

  return (
    <div className="bg-white p-8 text-[#111812]" data-testid="plan-summary" style={{ fontFamily: "Outfit, sans-serif" }}>
      <style>{PRINT_CSS}</style>
      <div className="pb-no-print mb-8 flex flex-wrap gap-3">
        <button onClick={() => window.print()} data-testid="pb-print"
          className="rounded-full bg-[#1A3626] px-6 py-2 text-sm text-[#F9F7F1]">Print / Save as PDF</button>
        <button onClick={() => setDollars(!dollars)} data-testid="pb-toggle-dollars"
          className="rounded-full border border-[#1A3626] px-6 py-2 text-sm text-[#1A3626]">
          {dollars ? "Hide" : "Show"} dollar estimates
        </button>
        <button onClick={() => setShowAssumptions(!showAssumptions)} data-testid="pb-toggle-assumptions"
          className="rounded-full border border-[#1A3626] px-6 py-2 text-sm text-[#1A3626]">
          {showAssumptions ? "Hide" : "Include"} assumptions
        </button>
        <Link to={`/admin/plan-builder/scenarios/${sc.id}`} data-testid="pb-back-builder"
          className="rounded-full border border-[#1A3626] px-6 py-2 text-sm text-[#1A3626]">Back to builder</Link>
      </div>

      <header className="pb-card border-b border-[#D8D2C4] pb-6">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#C15C3D]">OneHarvestCo. Benefits</p>
        <h1 className="mt-3 text-3xl font-light text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Prepared for {sc.client_company}
        </h1>
        <p className="mt-2 text-sm text-[#4A524D]">
          Plan year {inputs.year} · Prepared {prepared} by Kyle Walker
        </p>
      </header>

      <section className="pb-card mt-8">
        <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Employer snapshot</h2>
        <dl className="mt-3 grid gap-3 sm:grid-cols-2">
          {[["Enrolled employees", m.n], ["Full-time equivalents", Math.round(m.fte)],
            ["State", inputs.state === "TX" ? "Texas" : "Other state"], ["Industry", industry],
            ["Current premium PEPM", dollars ? money(Number(inputs.pepm)) : "shared separately"],
            ["Renewal increase", `${inputs.renewal}%`],
            ["Contribution strategy", inputs.mode === "percent"
              ? `Percent of premium (${inputs.pctEE}% employee, ${inputs.pctDep}% dependent)`
              : `Flat employer contribution${dollars ? ` of ${money(Number(inputs.flat))}/mo` : ""}`],
            ["Priorities", priorities], ["Risk tolerance", inputs.risk]].map(([t, v]) => (
            <div key={t}>
              <dt className="text-[11px] uppercase tracking-[0.12em] text-[#4A524D]">{t}</dt>
              <dd className="text-[13px]">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-8">
        <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Top options</h2>
        {t3.map((r, i) => (
          <div key={r.a.id} className="pb-card mt-5 border border-[#D8D2C4] p-5" data-testid={`pb-summary-option-${r.a.id}`}>
            <h3 className="text-lg text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {i + 1}. {r.a.name}
            </h3>
            <p className="mt-2 text-[13px] leading-relaxed">{reason(r, inputs.risk, m.tol, inputs)}</p>
            <p className="mt-2 text-[13px] text-[#4A524D]">{r.elig}: {r.note}</p>
            {dollars ? (
              <p className="mt-2 text-[13px]">
                Estimated total {money(r.cost.total)} · employer {money(r.cost.employer)} · employee{" "}
                {money(r.cost.employee)} per year · {signed(r.savTot)} vs renewal
              </p>
            ) : null}
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {[["Pros", r.a.pros], ["Cons", r.a.cons]].map(([t, list]) => (
                <div key={t}>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-[#4A524D]">{t}</p>
                  <ul className="mt-1 list-disc pl-4 text-[13px] leading-relaxed">
                    {list.map((x) => <li key={x}>{x}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {dollars ? (
        <section className="pb-card mt-8">
          <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Comparison</h2>
          <table className="mt-3 w-full">
            <thead><tr>{["Arrangement", "Status", "Employer / yr", "Employee / yr", "Total / yr", "vs renewal"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {m.results.filter((r) => r.elig !== "Not eligible").map((r) => (
                <tr key={r.a.id}>
                  <td className={td}>{r.a.name}</td>
                  <td className={td}>{r.elig}</td>
                  <td className={td}>{money(r.cost.employer)}</td>
                  <td className={td}>{money(r.cost.employee)}</td>
                  <td className={td}>{money(r.cost.total)}</td>
                  <td className={td}>{signed(r.savTot)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <section className="pb-card mt-8">
        <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Compliance checks</h2>
        <ul className="mt-3 list-disc space-y-2 pl-4 text-[13px] leading-relaxed">
          <li>{m.small
            ? (inputs.state === "TX"
              ? "Small group: Texas defines small employers as 2–50 employees (Tex. Ins. Code §1501.002)."
              : "Small group: 50 or fewer employees in most states.")
            : "Large group: 51+ employees, experience-rated."}</li>
          <li>{m.ale
            ? `${Math.round(m.fte)} FTEs: employer mandate applies. No-offer exposure under 4980H(a) is${dollars ? ` about ${money(m.penaltyA)} a year at the 2026 rate of ${money(3340)} per FT employee beyond 30` : " set per full-time employee beyond the first 30, at the published 2026 rate"}.`
            : `${Math.round(m.fte)} FTEs: under 50, so no employer mandate penalty, and QSEHRA is available if no group plan is offered.`}</li>
          {dollars ? (
            <li>{`Employee contribution about ${money(m.coreEE)}/mo vs a ${(m.affPct * 100).toFixed(2)}% limit of ${money(m.affLimit)}/mo on a ${money(Number(inputs.wage))} wage (rate-of-pay safe harbor). ${m.ale ? "Required for ALEs." : "Informational under 50 FTEs."}`}</li>
          ) : null}
          <li>{`About ${Math.round(m.lives)} covered lives are subject to the PCORI fee, filed by the employer on Form 720 by July 31. Insured plans: the carrier pays.`}</li>
          {dollars ? (
            <li>{`${inputs.year}: HSA-qualified plans need a minimum deductible of ${hsa.ded} with an out-of-pocket max no higher than ${hsa.oop}; HSA limit ${hsa.hsa} (self-only / family).`}</li>
          ) : (
            <li>{`${inputs.year}: HSA-qualified plans must meet the published minimum deductible, out-of-pocket maximum and contribution limits for the plan year.`}</li>
          )}
        </ul>
      </section>

      {sc.advisor_notes ? (
        <section className="pb-card mt-8">
          <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Advisor notes</h2>
          <p className="mt-3 whitespace-pre-wrap text-[13px] leading-relaxed">{sc.advisor_notes}</p>
        </section>
      ) : null}

      <section className="pb-card mt-8">
        <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>What I&rsquo;ll need from you</h2>
        <ul className="mt-3 list-disc space-y-1 pl-4 text-[13px] leading-relaxed">
          {CHECKLIST.map(([group, items]) => <li key={group}>{group}: {items.length} items</li>)}
        </ul>
      </section>

      {showAssumptions ? (
        <section className="pb-card mt-8">
          <h2 className="text-xl text-[#1A3626]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Assumptions</h2>
          <ul className="mt-3 space-y-1 text-[12px]">{FORMULAS.map((f) => <li key={f}>{f}</li>)}</ul>
          <table className="mt-4 w-full">
            <thead><tr>{["Assumption", "Value used"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {ASSUMPTIONS.map(([a, value]) => (
                <tr key={a}>
                  <td className={td}>{a}</td>
                  <td className={td}>{value === "__HYBRID__"
                    ? `${Math.round(m.s * 100)}% of enrollees in the ICHRA class, rest on the core group plan`
                    : (dollars ? value : value.replace(/\$[\d,]+/g, "see rate sheet"))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <footer className="pb-card mt-10 border-t border-[#D8D2C4] pt-5">
        <p className="text-[11px] text-[#4A524D]">
          Kyle Walker · Licensed Texas Health Insurance Agent · OneHarvestCo. Benefits · (713) 725-7413 ·
          kyle@ohcbenefits.com · ohcbenefits.com
        </p>
        <p className="mt-3 text-[11px] leading-relaxed text-[#4A524D]">
          Illustrative planning estimates, not quotes or guarantees of cost, savings or eligibility.
          Figures use relative cost factors applied to your current renewal and published industry
          ranges. Actual rates and eligibility depend on carrier, TPA and stop-loss underwriting of your
          census and claims.
        </p>
      </footer>
    </div>
  );
}
